/**
 * Listening to a YouTube song before its own file is here.
 *
 * Nothing is downloaded in the shopper's browser. The song plays in YouTube's
 * own embedded player (the IFrame API, on the no-cookie host), kept small — a
 * thumbnail-sized video in the song's card, since the shopper is choosing a
 * song to hear, not a video to watch. The part is marked on the cutter under
 * the card (waveform.ts), which plays the song through this player until the
 * song's own file arrives and takes over; then this player retires to its
 * thumbnail, so only one thing ever plays the song.
 *
 * If YouTube's script cannot load (blocked, offline) the card still works: a
 * plain embed, full width so its own controls fit, plays the song, and the
 * part is marked on the cutter by hand.
 */

import type { CutterTransport } from './waveform.js';

const TEXTS = {
  tapVideo: 'Ako pesma ne krene, dodirni sličicu videa.',
  loading: 'Učitavamo plejer…',
  blocked: 'Ne pušta se ovde',
};

export interface YoutubePlayerOptions {
  videoId: string;
  title?: string;
  /**
   * false: the video is known not to play here. No player is loaded; the
   * thumbnail stays, marked as not playing here.
   */
  playable?: boolean;
  /**
   * YouTube refused to play the video here (its owner forbids other sites, or
   * it is age- or region-restricted).
   */
  onBlocked?: () => void;
  /** The player loaded: the length and the title YouTube itself gives. */
  onReady?: (info: { durationMs?: number; title?: string }) => void;
  /** The shopper started the song here: every other player stops. */
  onPlay?: () => void;
}

export interface YoutubePlayer extends CutterTransport {
  /** The video: its thumbnail until the player loads, then the player itself. */
  frame: HTMLElement;
  /** "Tap the video" — a phone that refused a start that did not come from the video. */
  hint: HTMLElement;
  /** YouTube's plain embed stands in: it has its own controls and cannot be driven. */
  isPlain(): boolean;
  /**
   * Plays [fromMs, toMs) once, for "Preslušaj sve". Resolves true when it
   * reached its end, false when it was stopped or could not play. `onStall`
   * runs when the player has not started after a moment — a phone that wants
   * a tap on the video itself.
   */
  playSegment(fromMs: number, toMs: number | undefined, onStall?: () => void): Promise<boolean>;
  stopSegment(): void;
  /**
   * The song's own file plays now, or — `blocked` — YouTube will not play it
   * here (the server's check said so first): the thumbnail stays, the player goes.
   */
  retire(blocked?: boolean): void;
  destroy(): void;
}

/** The video id in any link a phone's share sheet produces, or null. */
export function parseYoutubeId(input: string): string | null {
  const text = input.trim();
  if (!/^https?:\/\//i.test(text)) return null;
  let url: URL;
  try {
    url = new URL(text);
  } catch {
    return null;
  }
  const host = url.hostname.replace(/^(www|m|music)\./, '');
  let id: string | null = null;
  if (host === 'youtu.be') id = url.pathname.slice(1).split('/')[0] ?? null;
  else if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
    if (url.pathname === '/watch') id = url.searchParams.get('v');
    else {
      const match = /^\/(?:shorts|embed|live|v)\/([^/?#]+)/.exec(url.pathname);
      id = match?.[1] ?? null;
    }
  }
  return id && /^[\w-]{11}$/.test(id) ? id : null;
}

/** m:ss, the way shoppers write song times. */
export function clock(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/** The still YouTube serves for a video. */
export function youtubeThumbnail(videoId: string): string {
  return `https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`;
}

// ── The IFrame API ─────────────────────────────────────────────────────────────

interface YtPlayer {
  playVideo(): void;
  pauseVideo(): void;
  seekTo(seconds: number, allowSeekAhead: boolean): void;
  getCurrentTime(): number;
  getDuration(): number;
  getPlayerState(): number;
  getVideoData?(): { title?: string };
  destroy(): void;
}

interface YtNamespace {
  Player: new (
    element: HTMLElement,
    options: {
      videoId: string;
      host?: string;
      width?: string | number;
      height?: string | number;
      playerVars?: Record<string, string | number>;
      events?: {
        onReady?: (event: { target: YtPlayer }) => void;
        onStateChange?: (event: { data: number; target: YtPlayer }) => void;
        onError?: (event: { data: number }) => void;
      };
    },
  ) => YtPlayer;
  ready?: (callback: () => void) => void;
}

type YtWindow = Window & { YT?: YtNamespace; onYouTubeIframeAPIReady?: () => void };

let apiLoading: Promise<YtNamespace | null> | null = null;

/** Loads YouTube's player script once per page; null when it cannot load. */
export function loadYoutubeApi(timeoutMs = 12_000): Promise<YtNamespace | null> {
  const win = window as YtWindow;
  if (win.YT?.Player) return Promise.resolve(win.YT);
  if (apiLoading) return apiLoading;
  apiLoading = new Promise<YtNamespace | null>((resolve) => {
    let settled = false;
    const done = (value: YtNamespace | null): void => {
      if (settled) return;
      settled = true;
      if (!value) apiLoading = null;
      resolve(value);
    };
    // Another plugin on the page may be loading it already: join in.
    if (win.YT && typeof win.YT.ready === 'function') {
      win.YT.ready(() => done(win.YT ?? null));
    } else {
      const previous = win.onYouTubeIframeAPIReady;
      win.onYouTubeIframeAPIReady = () => {
        previous?.();
        done(win.YT ?? null);
      };
      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      script.async = true;
      script.onerror = () => done(null);
      document.head.appendChild(script);
    }
    setTimeout(() => done(win.YT?.Player ? win.YT : null), timeoutMs);
  });
  return apiLoading;
}

// ── The player ────────────────────────────────────────────────────────────────

export function createYoutubePlayer(options: YoutubePlayerOptions): YoutubePlayer {
  const { videoId } = options;

  let player: YtPlayer | null = null;
  let playerReady = false;
  let blocked = options.playable === false;
  let retired = false;
  let destroyed = false;
  /** YouTube's plain embed stands in: its own controls, so it keeps its size. */
  let plain = false;
  let playing = false;
  let tapTimer: number | undefined;
  const listeners = new Set<() => void>();
  const notify = (): void => {
    for (const listener of [...listeners]) listener();
  };

  /** The running "Preslušaj sve" part, resolved when it ends or is stopped. */
  let segmentDone: ((finished: boolean) => void) | null = null;
  let segmentUntil: number | null = null;
  let segmentTimer: number | undefined;
  let stallTimer: number | undefined;
  const finishSegment = (finished: boolean): void => {
    window.clearTimeout(stallTimer);
    window.clearInterval(segmentTimer);
    segmentTimer = undefined;
    segmentUntil = null;
    const done = segmentDone;
    segmentDone = null;
    done?.(finished);
  };

  // The video as a small thumbnail: its still shows until the player loads.
  const frame = document.createElement('div');
  frame.className = 'pz-yt-frame is-compact';
  frame.style.backgroundImage = `url("${youtubeThumbnail(videoId)}")`;
  const mount = document.createElement('div');
  frame.appendChild(mount);
  const badge = document.createElement('span');
  badge.className = 'pz-yt-badge';
  badge.hidden = true;
  frame.appendChild(badge);

  const hint = document.createElement('p');
  hint.className = 'pz-hint pz-yt-tap';
  hint.textContent = TEXTS.tapVideo;
  hint.hidden = true;

  const ready = (): boolean => playerReady && !blocked && !retired && !destroyed;

  const showState = (): void => {
    frame.classList.toggle('is-loading', !playerReady && !blocked && !retired && !plain && !destroyed);
    frame.classList.toggle('is-still', blocked || retired);
    badge.hidden = !blocked;
    badge.textContent = blocked ? TEXTS.blocked : '';
    frame.title = !playerReady && !blocked && !retired && !plain ? TEXTS.loading : '';
    if (playing) {
      window.clearTimeout(tapTimer);
      hint.hidden = true;
    }
  };

  /** The iframe goes (that is what stops it for sure); the still stays. */
  const dropPlayer = (): void => {
    try {
      player?.destroy();
    } catch {
      // Already gone with its iframe.
    }
    player = null;
    playing = false;
    for (const child of [...frame.children]) if (child !== badge) child.remove();
  };

  /**
   * Starts the song. A phone may refuse a start that did not come from a tap on
   * the video itself; then the shopper is told to tap it.
   */
  const play = (): void => {
    if (!ready()) return;
    player!.playVideo();
    window.clearTimeout(tapTimer);
    tapTimer = window.setTimeout(() => {
      if (!destroyed && !retired && !playing) hint.hidden = false;
    }, 1800);
  };

  const pause = (): void => {
    window.clearTimeout(tapTimer);
    if (ready() && playing) player!.pauseVideo();
  };

  const plainEmbed = (): void => {
    // No IFrame API: YouTube's player still plays the song; the part is marked by hand.
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.youtube-nocookie.com/embed/${videoId}?playsinline=1&rel=0`;
    iframe.title = options.title ?? 'YouTube';
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
    iframe.allowFullscreen = true;
    iframe.loading = 'lazy';
    mount.replaceWith(iframe);
    plain = true;
    frame.classList.remove('is-compact');
    frame.classList.add('is-plain');
    showState();
    notify();
  };

  if (blocked) mount.remove();
  showState();

  void (blocked ? Promise.resolve(null) : loadYoutubeApi()).then((YT) => {
    if (destroyed || blocked || retired) return;
    if (!YT) {
      plainEmbed();
      return;
    }
    try {
      player = new YT.Player(mount, {
        videoId,
        host: 'https://www.youtube-nocookie.com',
        width: '100%',
        height: '100%',
        playerVars: { playsinline: 1, rel: 0, modestbranding: 1, origin: window.location.origin },
        events: {
          onReady: (event) => {
            if (destroyed || retired) return;
            playerReady = true;
            const measured = Math.round(event.target.getDuration() * 1000);
            const data = event.target.getVideoData?.();
            showState();
            options.onReady?.({ durationMs: measured > 0 ? measured : undefined, title: data?.title || undefined });
            notify();
          },
          onStateChange: (event) => {
            // 1 playing, 3 buffering: the song is on its way.
            const on = event.data === 1 || event.data === 3;
            // Started on the video itself (YouTube's own controls): the step's
            // other players stop too — but never a "Preslušaj sve" run, which
            // is what started it.
            if (on && !playing && !segmentDone) options.onPlay?.();
            playing = on;
            if (on) window.clearTimeout(stallTimer);
            // 0: the song ran to its end during a "Preslušaj sve".
            if (event.data === 0) finishSegment(true);
            showState();
            notify();
          },
          onError: (event) => {
            // 101/150: the owner does not allow embedding; 100: removed/private.
            if ([100, 101, 150, 153].includes(event.data)) {
              blocked = true;
              playerReady = false;
              dropPlayer();
              showState();
              finishSegment(false);
              notify();
              options.onBlocked?.();
            }
          },
        },
      });
    } catch {
      plainEmbed();
    }
  });

  const playSegment = async (fromMs: number, toMs: number | undefined, onStall?: () => void): Promise<boolean> => {
    finishSegment(false);
    // The player may still be loading (a restored song): wait a little.
    for (let waited = 0; !playerReady && !blocked && !destroyed && !retired && waited < 8000; waited += 200) {
      await new Promise((resolve) => setTimeout(resolve, 200));
    }
    if (!ready()) return false;
    return new Promise<boolean>((resolve) => {
      segmentDone = resolve;
      segmentUntil = toMs != null && toMs > fromMs ? toMs : null;
      player!.seekTo(fromMs / 1000, true);
      play();
      // The part's end, checked a few times a second.
      segmentTimer = window.setInterval(() => {
        if (!ready() || segmentUntil === null) return;
        if (player!.getCurrentTime() * 1000 >= segmentUntil) {
          player!.pauseVideo();
          finishSegment(true);
        }
      }, 200);
      stallTimer = window.setTimeout(() => {
        if (!playing && segmentDone === resolve) onStall?.();
      }, 2500);
    });
  };

  return {
    frame,
    hint,
    isPlain: () => plain,
    ready,
    playing: () => ready() && playing,
    positionMs: () => (ready() ? player!.getCurrentTime() * 1000 : 0),
    play,
    pause,
    seek: (ms) => {
      if (ready()) player!.seekTo(ms / 1000, true);
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    playSegment,
    stopSegment: () => {
      pause();
      finishSegment(false);
    },
    retire: (refused) => {
      if (destroyed) return;
      if (refused) blocked = true;
      if (retired) {
        showState();
        return;
      }
      pause();
      finishSegment(false);
      retired = true;
      playerReady = false;
      window.clearTimeout(tapTimer);
      hint.hidden = true;
      dropPlayer();
      // Back to the compact still, whatever the plain embed had made it.
      plain = false;
      frame.classList.remove('is-plain');
      frame.classList.add('is-compact');
      showState();
      notify();
    },
    destroy: () => {
      finishSegment(false);
      destroyed = true;
      window.clearTimeout(tapTimer);
      listeners.clear();
      dropPlayer();
    },
  };
}
